import React, { useCallback, useEffect } from "react";
import { usePrevious } from "react-use";
import { Undo } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Button, Divider, Typography } from "@mui/material";

import { LoadingButton } from "ui";

import { useMultiLayerEdit } from "fond/draw/MultiLayerEdit/useMultiLayerEdit";
import { useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

/**
 * Inner component that uses the MultiLayerEdit context
 */
const DataPrepDrawContent: React.FC = () => {
  const { goBack } = useStackedNavigationContext();
  const { isSaving, allComplete, save, reset } = useMultiLayerEdit();

  const prevIsSaving = usePrevious(isSaving);

  useEffect(() => {
    if (prevIsSaving && !isSaving) {
      // Import has completed (transitioned from true to false)
      // Check if all imports completed successfully (not errors)
      if (allComplete) {
        goBack();
      }
    }
  }, [prevIsSaving, isSaving, allComplete, goBack]);

  const handleBack = useCallback(() => {
    if (isSaving) return; // Prevent going back while saving is in progress
    goBack();
  }, [goBack, isSaving]);

  const handleOnSave = useCallback(async () => {
    await save();
  }, [save]);

  const handleOnReset = useCallback(() => {
    reset();
  }, [reset]);

  return (
    <>
      <StackedNavigationHeader onBackButtonClick={handleBack} title="Draw on map" />
      <Alert severity="info" data-testid="layer-alert">
        <AlertTitle sx={{ mb: 0 }}>Draw</AlertTitle>
      </Alert>
      <Divider sx={{ mt: 3 }} />
      <Button fullWidth sx={{ my: 0.5, justifyContent: "left" }} startIcon={<Undo sx={{ height: 20 }} />} onClick={handleOnReset} disabled={isSaving}>
        <Typography color={(theme) => theme.palette.common.black} fontWeight={500} fontSize={13}>
          Reset
        </Typography>
      </Button>
      <Divider />
      <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
        <Button color="primary" size="small" onClick={handleBack} disabled={isSaving}>
          Cancel
        </Button>
        <LoadingButton variant="contained" size="small" onClick={handleOnSave} sx={{ ml: 1, px: 2 }} data-testid="finish-button" loading={isSaving}>
          Finish
        </LoadingButton>
      </Box>
    </>
  );
};

export default DataPrepDrawContent;
